import * as React from "react";
import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { IMedia } from "src/model/unityObject";
import Player360 from "./Player360";
import Model3dViewerWeb from "./Model3dViewerWeb";
import InteractiveViewer360 from "../../statsNew/components/gazeHeatmap/InteractiveViewer360";
import VideoControls from "../../statsNew/components/gazeHeatmap/VideoControls";
import { prettifyFileSizeInBytes } from "src/common/util/prettifyFileSizeInBytes";
import { ILanguage } from "src/model/model";
import { languageConfig } from "src/common/util/langConfig";
import { useSelector, useDispatch } from "react-redux";
import { getCurrentUser } from "src/features/profile/state/profileSlice";
import { prettifyMediaType } from "src/common/util/prettifyMediaType";
import { useTranslation } from "react-i18next";
import apiUrls from "src/api";
import { jwtSelector } from "src/common/state/selectors/authSelector";
import {
  getShowMediaPreviewModal,
  closeMediaPreviewModal,
} from "src/common/state/slice/modal/modalSlice";
import { AppDispatch } from "src/common/state/store";
import "../../statsNew/components/statsNew.css";

interface IModalPreviewProps {
  media: IMedia;
  onCloseCb(arg?: any): any;
}

export const ModalPreview: React.FC<IModalPreviewProps> = ({ media, onCloseCb }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const currentUser = useSelector(getCurrentUser);
  const jwt = useSelector(jwtSelector);
  const currentLocale = languageConfig((currentUser?.preferred_language as ILanguage)?.name);

  // Check if this modal should be shown (controlled by Redux)
  const modalState = useSelector(getShowMediaPreviewModal);
  const modalId = `preview_${media.id}`;
  const isOpen = modalState.show && modalState.id === modalId;

  // Video controls state (for media360 videos)
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const lastTimeUpdateRef = useRef(0);

  // Check if this is a media360 video
  const isMedia360Video = media.media_360_tag === "media360" && media.type === "video";

  // Use a ref to track if duration was set (to avoid dependency on videoDuration state)
  const durationSetRef = useRef(false);

  // Handle video time updates from InteractiveViewer360
  // Throttle state updates to ~4 times per second to avoid excessive re-renders
  // Also get duration from the video element when available
  const handleTimeUpdate = useCallback((currentTime: number) => {
    const now = Date.now();
    if (now - lastTimeUpdateRef.current > 250) {
      lastTimeUpdateRef.current = now;
      setVideoCurrentTime(currentTime);
      // Get duration if not set yet (use ref to avoid recreating callback)
      if (!durationSetRef.current && videoRef.current && videoRef.current.duration > 0) {
        durationSetRef.current = true;
        setVideoDuration(videoRef.current.duration);
      }
    }
  }, []);

  const handleDownload = async () => {
    try {
      const response = await fetch(`${apiUrls.medias.download}/${media.id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!response.ok || !response.body) {
        throw new Error("Download failed.");
      }

      const reader = response.body.getReader();
      const chunks = [];
      let done = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        if (value) chunks.push(value);
        done = readerDone;
      }

      const blob = new Blob(chunks);
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = media.filename || "downloaded_file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      console.error("❌ Error downloading file:", error);
    }
  };

  // Compute media URL for media360 (cascade: preset2 → preset1 → raw)
  // Support both field naming conventions: with underscore (from standard Strapi API) and without (from custom controller)
  // Memoize to prevent unnecessary video reloads on re-render
  const media360Url = useMemo(() => {
    const mediaAny = media as any;
    const preset2 = media.s3_url_ffmpeg_preset2 ?? mediaAny.s3_url_ffmpeg_preset_2;
    const preset1 = media.s3_url_ffmpeg_preset1 ?? mediaAny.s3_url_ffmpeg_preset_1;
    return preset2 ?? preset1 ?? media.s3_url;
  }, [media.id, media.s3_url, media.s3_url_ffmpeg_preset1, media.s3_url_ffmpeg_preset2]);

  // Render function for non-360 media types (no state updates, safe to recreate)
  const renderOtherMedia = () => {
    if (media.type === "image") {
      return <img className="object-contain w-full h-full" src={media.s3_url} alt="image-media" />;
    }
    if (media.type === "video") {
      return (
        <video controls autoPlay className="w-full h-full">
          <source src={media.s3_url} type="video/mp4" />
        </video>
      );
    }
    if (media.type === "audio") {
      return (
        <div className="flex items-center justify-center w-full h-full">
          <audio id="myAudio" className="w-full max-w-2xl" controls autoPlay>
            <source src={media.s3_url} />
          </audio>
        </div>
      );
    }
    if (media.type === "pdf") {
      return <p>coming soon(tm)</p>;
    }
    if (media.type === "model3d") {
      return (
        <Model3dViewerWeb
          modelUrl={media.s3_url}
          className="w-full h-full"
          canvasClassName="rounded-2xl"
        />
      );
    }
    if (media.type === "avatar") {
      return (
        <img
          className="object-contain w-full h-full"
          src={media.thumbnail_s3_url}
          alt="image-media"
        />
      );
    }
    return (
      <img
        className="object-contain h-full w-full"
        src="https://d1nhio0ox7pgb.cloudfront.net/_img/g_collection_png/standard/512x512/link_broken.png"
        alt="Broken link"
      />
    );
  };

  // Close modal function
  const handleClose = useCallback(() => {
    dispatch(closeMediaPreviewModal(modalId));
    onCloseCb?.();
  }, [dispatch, modalId, onCloseCb]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose]);

  // Don't render if modal is not open
  if (!isOpen) {
    return null;
  }

  // Get media type badge info
  const mediaTypeInfo = prettifyMediaType({ type: media.type, media_360_tag: media.media_360_tag });

  return createPortal(
    <div
      className="stats-modal-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="media-preview-title"
    >
      <div
        className="stats-modal-content w-full max-w-5xl h-[80vh] max-h-[800px] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {/* Title and metadata */}
            <div className="min-w-0 flex-1">
              <h2
                id="media-preview-title"
                className="text-lg font-semibold text-gray-900 truncate"
                title={media.name}
              >
                {media.name}
              </h2>
              <div className="flex items-center gap-3 mt-0.5">
                <span className="text-sm text-gray-500">
                  {prettifyFileSizeInBytes(Number(media.size_in_bytes), currentLocale)}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  {mediaTypeInfo}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Download button */}
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              <span className="text-sm font-medium">{t("general.download")}</span>
            </button>

            {/* Close button */}
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label={t("general.close")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Media Preview Container */}
        <div className="flex-1 min-h-0 p-4">
          <div className="relative w-full h-full bg-black rounded-xl overflow-hidden">
            {/* Player360 (Unity WebGL) for video180stereo - stereo rendering not supported by Three.js */}
            {media.media_360_tag === "video180stereo" && media.type !== "model3d" && (
              <Player360
                mediaUrl={media.s3_url}
                mediaType={media.type as "image" | "video"}
                mediaTag360={media.media_360_tag as "media2d" | "media360" | "video180stereo"}
                className="w-full h-full object-contain"
                canvasClassName="rounded-xl"
                disableFullScreen
              />
            )}

            {/* InteractiveViewer360 (Three.js) for media360 */}
            {media.media_360_tag === "media360" && media.type !== "model3d" && (
              <InteractiveViewer360
                key={media.id}
                mediaUrl={media360Url}
                mediaType={isMedia360Video ? "video" : "image"}
                heatmapImageData={null}
                opacity={0}
                videoRef={videoRef}
                onTimeUpdate={isMedia360Video ? handleTimeUpdate : undefined}
              />
            )}

            {/* Other media types */}
            {media.media_360_tag !== "video180stereo" &&
              media.media_360_tag !== "media360" &&
              renderOtherMedia()}

            {/* Video Controls for media360 videos */}
            {isMedia360Video && videoDuration > 0 && (
              <div className="absolute bottom-4 left-4 right-4 z-10">
                <VideoControls
                  videoRef={videoRef}
                  currentTime={videoCurrentTime}
                  duration={videoDuration}
                  segmentStart={0}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
